/**
 * Portal text: staff reword the portal's UI strings (customer, partner and admin surfaces, English and Arabic) without a
 * deploy. Lists the editable catalog keys with the shipped text; each locale has an editable wording, a status, and
 * Save draft / Publish / Reset to shipped. The API enforces every rule (key in the catalogs, not protected, plain text,
 * the same {placeholders}); the screen mirrors the text rules for fast feedback. Docs: docs/specs/portal-text-cms.md.
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Button, Text } from '@qm/ui';
import { UI_TEXT_MAX_LENGTH, UI_TEXT_SURFACES, type UiTextItemView, type UiTextSurface } from '@qm/contracts/cms-text';
import type { CmsLocale } from '@qm/contracts/cms';
import type { Translate } from '@qm/i18n';
import { loadUiText } from '../../../lib/ui-text';
import { useSurface } from '../kit/context';
import { errorDetails, errorInfo, type Envelope } from '../kit/http';
import { useAction, useResource } from '../kit/resource';
import { Badge, errorMessage, Field, Loadable, Notice, Screen, Tabs, When } from '../kit/ui';
import {
  canEditUiText, canViewUiText, UI_TEXT_PAGE_SIZE, UI_TEXT_STATUS_TONE, uiTextActions, uiTextDirty, uiTextProblem, uiTextProblemKey, uiTextStart, uiTextStatus,
} from '../lib/cms-logic';
import type { UiTextListMeta } from '@qm/contracts/cms-text';
import { useAdminApi } from './layout';
import { ConfirmButton } from './cms-shared';

const K = 'portal.admin.cms.text.';
const VIEWS = ['both', 'en', 'ar'] as const;
type View = (typeof VIEWS)[number];
const STATUS_FILTERS = ['draft', 'published', 'none'] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number];
type Listing = Envelope<UiTextItemView[]> & { meta: UiTextListMeta };

/** After a change the staff member's own console shows the new wording straight away (the public copy is otherwise cached ~60 s). */
const refreshOverlay = () => { void loadUiText('en', { force: true }); void loadUiText('ar', { force: true }); };

/** Translated message for a failed Portal text call: the API's reason first, then the generic kit mapping. */
export function uiTextErrorText(t: Translate, err: unknown): string {
  const info = errorInfo(err);
  if (info.reason) {
    const d = errorDetails(err);
    const o = d && !Array.isArray(d) ? d : {};
    const list = (v: unknown) => (Array.isArray(v) && v.length ? v.map((p) => `{${String(p)}}`).join(', ') : t(`${K}none`));
    const k = `${K}err.${info.reason}`;
    const msg = t(k, { max: UI_TEXT_MAX_LENGTH, missing: list(o.missing), extra: list(o.extra) });
    if (msg !== k) return msg;
  }
  return errorMessage(t, err);
}

function TextError({ error, onReload }: { error: unknown; onReload?: () => void }) {
  const { t } = useSurface();
  if (!error) return null;
  const info = errorInfo(error);
  return (
    <div className="qx-alert" role="alert" data-reason={info.reason ?? undefined} data-testid="text-error">
      <Text>{uiTextErrorText(t, error)}</Text>
      {info.reason === 'stale_version' && onReload ? <Button onClick={onReload} data-testid="text-stale-reload">{t('portal.admin.cms.editor.reload')}</Button> : null}
    </div>
  );
}

export function CmsTextPage() {
  const { t, roles } = useSurface();
  const api = useAdminApi();
  const [sp, setSp] = useSearchParams();
  const surface = (UI_TEXT_SURFACES as readonly string[]).includes(sp.get('surface') ?? '') ? (sp.get('surface') as UiTextSurface) : '';
  const status = (STATUS_FILTERS as readonly string[]).includes(sp.get('status') ?? '') ? (sp.get('status') as StatusFilter) : '';
  const view = (VIEWS as readonly string[]).includes(sp.get('view') ?? '') ? (sp.get('view') as View) : 'both';
  const q = sp.get('q') ?? '';
  const page = Math.max(1, Math.floor(Number(sp.get('page')) || 1));
  const [draftQ, setDraftQ] = useState(q);
  const list = useResource<Listing>(`cms:text:${surface}:${status}:${view === 'both' ? '' : view}:${q}:${page}`, () => api.uiTextList({
    surface: surface || undefined, status: status || undefined, locale: view === 'both' ? undefined : view, q: q || undefined, limit: UI_TEXT_PAGE_SIZE, offset: (page - 1) * UI_TEXT_PAGE_SIZE,
  }) as Promise<Listing>);
  if (!canViewUiText(roles)) return <Screen title={t(`${K}title`)}><Notice testId="cms-no-access">{t(`${K}noAccess`)}</Notice></Screen>;
  const canEdit = canEditUiText(roles);
  const set = (next: Record<string, string>) => setSp(Object.fromEntries(Object.entries({ surface, status, view: view === 'both' ? '' : view, q, ...next }).filter(([, v]) => v)));
  const changed = () => { refreshOverlay(); list.reload(); };
  const drafts = list.status === 'ready' ? list.data.meta.drafts : 0;
  return (
    <Screen title={t(`${K}title`)} subtitle={t(`${K}subtitle`)} testId="cms-text" actions={canEdit ? <BulkPublish drafts={drafts} onDone={changed} /> : null}>
      <Notice testId="text-scope">{t(`${K}scope`)}</Notice>
      {canEdit ? null : <Notice testId="cms-read-only">{t(`${K}readOnly`)}</Notice>}
      <form className="qx-toolbar" role="search" onSubmit={(e) => { e.preventDefault(); set({ q: draftQ.trim(), page: '' }); }}>
        <Field label={t(`${K}search`)}>{(id) => <input id={id} name="q" type="search" value={draftQ} onChange={(e) => setDraftQ(e.target.value)} />}</Field>
        <Field label={t(`${K}surface.label`)}>
          {(id) => (
            <select id={id} name="surface" value={surface} onChange={(e) => set({ surface: e.target.value, page: '' })}>
              <option value="">{t(`${K}surface.all`)}</option>
              {UI_TEXT_SURFACES.map((s) => <option key={s} value={s}>{t(`${K}surface.${s}`)}</option>)}
            </select>
          )}
        </Field>
        <Field label={t(`${K}filter.label`)}>
          {(id) => (
            <select id={id} name="status" value={status} onChange={(e) => set({ status: e.target.value, page: '' })}>
              <option value="">{t(`${K}filter.all`)}</option>
              {STATUS_FILTERS.map((s) => <option key={s} value={s}>{t(`${K}filter.${s}`)}</option>)}
            </select>
          )}
        </Field>
        <Button type="submit" variant="primary" data-testid="text-search">{t(`${K}searchSubmit`)}</Button>
      </form>
      <Tabs label={t(`${K}locale.label`)} active={view} onSelect={(v) => set({ view: v === 'both' ? '' : v, page: '' })} tabs={VIEWS.map((v) => ({ id: v, label: t(`${K}locale.${v}`) }))} />
      <Loadable resource={list}>
        {({ data, meta }) => {
          const pages = Math.max(1, Math.ceil(meta.total / meta.limit));
          return (
            <>
              {data.length === 0 ? <Text tone="muted" data-testid="text-empty">{t(`${K}empty`)}</Text> : (
                <div data-testid="text-list">
                  {data.map((item) => <TextRow key={item.key} item={item} view={view} canEdit={canEdit} onChanged={changed} />)}
                </div>
              )}
              {meta.total > 0 ? (
                <nav className="qx-toolbar" aria-label={t(`${K}page`, { page, pages })} data-testid="text-pager">
                  <Button onClick={() => set({ page: String(page - 1) === '1' ? '' : String(page - 1) })} disabled={page <= 1}>{t(`${K}prev`)}</Button>
                  <Text tone="muted">{t(`${K}page`, { page, pages })} · {t(`${K}count`, { from: meta.offset + 1, to: Math.min(meta.offset + meta.limit, meta.total), total: meta.total })}</Text>
                  <Button onClick={() => set({ page: String(page + 1) })} disabled={page >= pages}>{t(`${K}next`)}</Button>
                </nav>
              ) : null}
            </>
          );
        }}
      </Loadable>
    </Screen>
  );
}

/** "Publish all drafts": two steps (ask, then confirm), shows how many went live and how many were skipped. */
export function BulkPublish({ drafts, onDone }: { drafts: number; onDone: () => void }) {
  const { t } = useSurface();
  const api = useAdminApi();
  const [asking, setAsking] = useState(false);
  const run = useAction(api.uiTextPublishAll);
  const r = run.result;
  return (
    <div className="qx-toolbar" data-testid="text-bulk">
      {asking ? (
        <span className="qx-confirm" role="group">
          <Text>{t(`${K}publishAllQuestion`, { n: drafts })}</Text>
          <Button variant="primary" disabled={run.pending} data-testid="text-publish-all-confirm" onClick={async () => { if (await run.run()) { setAsking(false); onDone(); } }}>{t(`${K}publishAllConfirm`)}</Button>
          <Button onClick={() => setAsking(false)}>{t('portal.admin.cms.cancel')}</Button>
        </span>
      ) : <Button variant="primary" disabled={drafts === 0} onClick={() => setAsking(true)} data-testid="text-publish-all">{t(`${K}publishAll`, { n: drafts })}</Button>}
      {r && !asking ? <Text role="status" data-testid="text-bulk-result">{t(`${K}publishedAll`, { n: r.published })}{r.skipped.length ? ` ${t(`${K}skippedNote`, { n: r.skipped.length })}` : ''}</Text> : null}
      <TextError error={run.error} />
    </div>
  );
}

function TextRow({ item, view, canEdit, onChanged }: { item: UiTextItemView; view: View; canEdit: boolean; onChanged: () => void }) {
  const { t } = useSurface();
  const locales: CmsLocale[] = view === 'both' ? ['en', 'ar'] : [view];
  return (
    <article className="qx-text-row" data-testid={`text-row-${item.key}`} aria-label={item.key}>
      <div className="qx-text-meta">
        <code className="qx-text-key" dir="ltr">{item.key}</code>
        {item.surfaces.map((s) => <Badge key={s}>{t(`${K}surface.${s}`)}</Badge>)}
        <Text tone="muted">{t(`${K}origin`, { origins: item.origins.join(', ') })}</Text>
      </div>
      <div className="qx-text-cols">
        {locales.map((l) => <LocaleEditor key={l} item={item} locale={l} canEdit={canEdit} onChanged={onChanged} />)}
      </div>
    </article>
  );
}

/** One key in one language: shipped text, editable wording, placeholder chips, status and the row actions. */
export function LocaleEditor({ item, locale, canEdit, onChanged }: { item: UiTextItemView; locale: CmsLocale; canEdit: boolean; onChanged: () => void }) {
  const { t } = useSurface();
  const api = useAdminApi();
  const o = item.override[locale];
  const shipped = item.shipped[locale];
  const [value, setValue] = useState(uiTextStart(shipped, o));
  const [done, setDone] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  // a save, publish or reset elsewhere (or after reload) brings new server state: show it
  useEffect(() => { setValue(uiTextStart(shipped, o)); }, [shipped, o?.version, o === null]);
  const save = useAction(() => api.uiTextSave(item.key, locale, value.trim(), o?.version ?? 0));
  const publish = useAction(() => api.uiTextPublish(item.key, locale));
  const reset = useAction(() => api.uiTextReset(item.key, locale));
  const status = uiTextStatus(o);
  const dirty = uiTextDirty(shipped, o, value);
  const problem = dirty ? uiTextProblem(shipped, value) : null;
  const can = uiTextActions(o, shipped, value, canEdit);
  const finish = (msg: string) => { setDone(msg); onChanged(); };
  const phs = item.placeholders[locale];
  const err = save.error ?? publish.error ?? reset.error;
  const problemText = problem ? t(uiTextProblemKey(problem), {
    max: UI_TEXT_MAX_LENGTH, missing: problem.missing?.length ? problem.missing.map((p) => `{${p}}`).join(', ') : t(`${K}none`), extra: problem.extra?.length ? problem.extra.map((p) => `{${p}}`).join(', ') : t(`${K}none`),
  }) : null;
  return (
    <section className="qx-text-col" aria-label={t(`${K}lang.${locale}`)} data-locale={locale} data-testid={`text-${item.key}-${locale}`}>
      <div className="qx-text-meta">
        <strong>{t(`${K}lang.${locale}`)}</strong>
        <Badge tone={UI_TEXT_STATUS_TONE[status]} testId={`text-status-${item.key}-${locale}`}>{t(`${K}status.${status}`)}</Badge>
        {dirty ? <Badge tone="warn" testId={`text-dirty-${item.key}-${locale}`}>{t(`${K}dirty`)}</Badge> : null}
      </div>
      <div>
        <Text tone="muted">{t(`${K}shipped`)}</Text>
        <p className="qx-text-shipped" dir={locale === 'ar' ? 'rtl' : 'ltr'} lang={locale} data-testid={`text-shipped-${item.key}-${locale}`}>{shipped}</p>
      </div>
      <Field label={t(`${K}wording`, { lang: t(`${K}lang.${locale}`) })} hint={t(`${K}length`, { n: value.length, max: UI_TEXT_MAX_LENGTH })} error={problemText}>
        {(id) => (
          <textarea id={id} name={`${item.key}:${locale}`} dir={locale === 'ar' ? 'rtl' : 'ltr'} lang={locale} value={value} readOnly={!canEdit} rows={3}
            onChange={(e) => { setValue(e.target.value); setDone(null); }} data-testid={`text-input-${item.key}-${locale}`} />
        )}
      </Field>
      <div className="qx-text-meta">
        <Text tone="muted">{t(`${K}placeholders`)}:</Text>
        {phs.length ? (
          <ul className="qx-chips" data-testid={`text-chips-${item.key}-${locale}`}>{phs.map((p) => <li key={p}><code dir="ltr">{`{${p}}`}</code></li>)}</ul>
        ) : <Text tone="muted">{t(`${K}placeholdersNone`)}</Text>}
      </div>
      {canEdit ? (
        <div className="qx-text-actions">
          <Button variant="primary" disabled={!can.save || save.pending} data-testid={`text-save-${item.key}-${locale}`}
            onClick={async () => { if (await save.run()) finish(t(`${K}saved`)); }}>{t(`${K}save`)}</Button>
          <Button disabled={!can.publish || publish.pending} data-testid={`text-publish-${item.key}-${locale}`}
            onClick={async () => { if (await publish.run()) finish(t(`${K}done.published`)); }}>{t(`${K}publish`)}</Button>
          {can.reset ? (
            <ConfirmButton label={t(`${K}reset`)} confirmLabel={t(`${K}resetConfirm`)} question={t(`${K}resetQuestion`)} disabled={reset.pending} testId={`text-reset-${item.key}-${locale}`}
              onConfirm={() => { void reset.run().then((r) => { if (r) finish(t(`${K}done.reset`)); }); }} />
          ) : null}
          {can.history ? <Button onClick={() => setShowHistory((v) => !v)} aria-expanded={showHistory} data-testid={`text-history-${item.key}-${locale}`}>{t(`${K}history`)}</Button> : null}
        </div>
      ) : (can.history ? <div className="qx-text-actions"><Button onClick={() => setShowHistory((v) => !v)} aria-expanded={showHistory} data-testid={`text-history-${item.key}-${locale}`}>{t(`${K}history`)}</Button></div> : null)}
      {done ? <Text role="status" data-testid={`text-done-${item.key}-${locale}`}>{done}</Text> : null}
      <TextError error={err} onReload={onChanged} />
      {showHistory && o ? <HistoryPanel item={item} locale={locale} canEdit={canEdit} publishedVersion={o.publishedVersion} stamp={`${o.version}:${o.publishedVersion ?? ''}`} onRolledBack={() => finish(t(`${K}done.rolledBack`))} /> : null}
    </section>
  );
}

function HistoryPanel({ item, locale, canEdit, publishedVersion, stamp, onRolledBack }: { item: UiTextItemView; locale: CmsLocale; canEdit: boolean; publishedVersion: number | null; stamp: string; onRolledBack: () => void }) {
  const { t } = useSurface();
  const api = useAdminApi();
  const hist = useResource(`cms:text:history:${item.key}:${locale}:${stamp}`, () => api.uiTextHistory(item.key, locale));
  const rollback = useAction((version: number) => api.uiTextRollback(item.key, locale, version));
  return (
    <div className="qx-text-history" data-testid={`text-history-list-${item.key}-${locale}`}>
      <Loadable resource={hist}>
        {(versions) => (versions.length === 0 ? <Text tone="muted">{t(`${K}historyNone`)}</Text> : (
          <ul className="qx-list">
            {versions.map((v) => (
              <li key={v.version} className="qx-text-version" dir="auto">
                <span className="qx-text-meta">
                  <strong>{t(`${K}version`, { n: v.version })}</strong>
                  {v.live ? <Badge tone="good">{t(`${K}live`)}</Badge> : null}
                  <When iso={v.createdAt} />
                </span>
                <span lang={locale} dir={locale === 'ar' ? 'rtl' : 'ltr'}>{v.value}</span>
                {canEdit && publishedVersion !== null && !v.live ? (
                  <Button disabled={rollback.pending} data-testid={`text-rollback-${item.key}-${locale}-${v.version}`} onClick={async () => { if (await rollback.run(v.version)) onRolledBack(); }}>{t(`${K}rollback`)}</Button>
                ) : null}
              </li>
            ))}
          </ul>
        ))}
      </Loadable>
      <TextError error={rollback.error} />
    </div>
  );
}
